import { Controller } from "@hotwired/stimulus"
import { convertReferenceChips } from "../lib/reference_chips"

export default class extends Controller {
  static targets = ["events", "activity", "scrollToTaskButton", "scrollToTaskIcon", "newActivityButton", "composerSpacer"]

  connect() {
    this.topRubberBandSuppressed = false
    this.lastTouchY = null
    this.initialAnchorActive = false

    this.boundBeforeStreamRender = this.beforeStreamRender.bind(this)
    this.boundOnScroll = this.onScroll.bind(this)
    this.boundOnWheel = this.onWheel.bind(this)
    this.boundOnTouchStart = this.onTouchStart.bind(this)
    this.boundOnTouchMove = this.onTouchMove.bind(this)

    document.addEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)
    window.addEventListener("scroll", this.boundOnScroll)
    window.addEventListener("resize", this.boundOnScroll)
    window.addEventListener("wheel", this.boundOnWheel, { passive: true })
    window.addEventListener("touchstart", this.boundOnTouchStart, { passive: true })
    window.addEventListener("touchmove", this.boundOnTouchMove, { passive: true })

    convertReferenceChips(this.element)
    this.setupComposerClearance()
    this.checkActionableTaskVisibility()
    if (!this.restoringHistory) this.scrollToInitialPosition()
  }

  // True while Turbo is rendering a history restoration visit.
  get restoringHistory() {
    return window.Turbo?.navigator?.currentVisit?.action === "restore"
  }

  disconnect() {
    window.cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = null
    document.removeEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)
    window.removeEventListener("scroll", this.boundOnScroll)
    window.removeEventListener("resize", this.boundOnScroll)
    window.removeEventListener("wheel", this.boundOnWheel)
    window.removeEventListener("touchstart", this.boundOnTouchStart)
    window.removeEventListener("touchmove", this.boundOnTouchMove)
    window.clearTimeout(this.topRubberBandTimeout)
    window.clearTimeout(this.initialAnchorTimeout)
    this.topRubberBandSuppressed = false
    this.initialAnchorActive = false
    this.composerResizeObserver?.disconnect()
  }

  onScroll() {
    if (this.scrollFrame != null) return
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null
      this.updateScrollControls()
    })
  }

  updateScrollControls() {
    if (window.scrollY > 0) this.clearTopRubberBandSuppression()

    this.checkActionableTaskVisibility()
    if (this.isNearPageBottom()) this.hideNewActivityButton()
  }

  onWheel(event) {
    this.releaseInitialAnchor()

    if (event.deltaY < 0 && window.scrollY <= 0) {
      this.suppressTopScrollToTaskButton()
    } else if (event.deltaY > 0) {
      this.clearTopRubberBandSuppression()
    }
  }

  onTouchStart(event) {
    this.lastTouchY = event.touches?.[0]?.clientY
  }

  onTouchMove(event) {
    const currentY = event.touches?.[0]?.clientY
    if (currentY == null || this.lastTouchY == null) return

    this.releaseInitialAnchor()

    if (currentY > this.lastTouchY && window.scrollY <= 0) {
      this.suppressTopScrollToTaskButton()
    } else if (currentY < this.lastTouchY) {
      this.clearTopRubberBandSuppression()
    }

    this.lastTouchY = currentY
  }

  beforeStreamRender(event) {
    const stream = event.target
    const targetId = stream.getAttribute("target")

    // The whole composer is replaced on every send (and re-rendered with, or
    // without, the blocker banner). Re-point the height observer at the new
    // element once it lands so the timeline's bottom reservation stays right.
    if (targetId === "paperwork_event_form") {
      const originalRender = event.detail.render
      event.detail.render = (streamElement) => {
        const result = originalRender(streamElement)
        Promise.resolve(result).then(() => this.observeComposer())
        return result
      }
      return
    }

    if (!this.affectsActivityStream(targetId)) return

    if (this.isDuplicateEventAppend(stream)) {
      event.preventDefault()
      this.afterStreamRender(false, false)
      return
    }

    const shouldFollow = this.shouldFollowStream()
    const addsContent = ["append", "prepend"].includes(stream.getAttribute("action"))
    const originalRender = event.detail.render

    event.detail.render = (streamElement) => {
      const result = originalRender(streamElement)

      Promise.resolve(result).then(() => this.afterStreamRender(shouldFollow, addsContent))

      return result
    }
  }

  affectsActivityStream(targetId) {
    if (!targetId) return false
    if (this.hasEventsTarget && targetId === this.eventsTarget.id) return true
    if (this.hasActivityTarget && targetId === this.activityTarget.id) return true

    const target = document.getElementById(targetId)
    if (!target || !this.element.contains(target)) return false

    return Boolean(target.closest(".paperwork-events") || target.classList.contains("paperwork-activity-slot"))
  }

  isDuplicateEventAppend(stream) {
    const action = stream.getAttribute("action")
    if (!["append", "prepend"].includes(action)) return false
    if (!this.hasEventsTarget) return false

    const template = stream.querySelector("template")
    const eventElement = template?.content?.querySelector("[data-event-id][id]")
    if (!eventElement) return false

    const eventId = eventElement.dataset.eventId
    const duplicateById = this.eventsTarget.querySelector(`#${CSS.escape(eventElement.id)}`)
    const duplicateByEventId = this.eventsTarget.querySelector(`[data-event-id="${CSS.escape(eventId)}"]`)

    return Boolean(duplicateById || duplicateByEventId)
  }

  afterStreamRender(shouldFollow, addsContent = true) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        convertReferenceChips(this.element)
        this.checkActionableTaskVisibility()
        if (shouldFollow) {
          this.scrollToLatestActivity()
        } else if (addsContent) {
          this.showNewActivityButton()
        }
      })
    })
  }

  // Follow the stream only when the reader is already pinned near the bottom.
  // Never yank the viewport away from someone reading earlier history.
  shouldFollowStream() {
    return this.isNearPageBottom()
  }

  isNearPageBottom() {
    const documentElement = document.documentElement
    const distanceFromBottom = documentElement.scrollHeight - (window.scrollY + window.innerHeight)
    return distanceFromBottom < 240
  }

  // Where the reader lands when the page first opens. Usually that is the
  // bottom of the timeline, but a trailing actionable task can be taller than
  // the viewport: the bottom of the page then shows the end of a form whose
  // title, description, and first fields were never seen. Anchor the top of
  // that card under the sticky header instead.
  //
  // Page entry only. Later events keep the follow-the-stream rules in
  // afterStreamRender, so a broadcast never re-anchors the viewport.
  scrollToInitialPosition() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
        const anchorTop = this.linkedTaskScrollTop() ?? this.trailingActionableTaskScrollTop()
        const top = anchorTop === null ? maxScrollTop : Math.min(anchorTop, maxScrollTop)

        // Only a card taller than the remaining viewport moves us off the
        // bottom; anything that already fits keeps the usual bottom landing.
        this.initialAnchorActive = top < maxScrollTop
        if (this.initialAnchorActive) this.holdInitialAnchor()

        window.scrollTo({ top, behavior: "auto" })
        this.hideNewActivityButton()
        this.checkActionableTaskVisibility()
      })
    })
  }

  // A task page's workflow card links here as #task-REF: land on that task's
  // latest row and ring it for a moment so the eye finds it.
  linkedTaskScrollTop() {
    const reference = window.location.hash.match(/^#task-([\w-]+)$/)?.[1]
    if (!reference) return null

    const root = this.hasEventsTarget ? this.eventsTarget : this.element
    const rows = Array.from(root.querySelectorAll(`[data-task-ref="${CSS.escape(reference)}"]`)).filter((row) => !row.hidden)
    const row = rows[rows.length - 1]
    if (!row) return null

    // The ring goes on the action card when the row has one: a widened card
    // reaches past the row's own box, so the row is the wrong shape to mark.
    const marked = row.querySelector(".inline-action-widenable") || row
    marked.classList.add("is-linked-task")
    window.setTimeout(() => marked.classList.remove("is-linked-task"), 2400)
    return this.taskAnchorScrollTop(row)
  }

  // Document offset that puts the top of the trailing actionable task just
  // under the sticky header, or null when the timeline does not end with one.
  trailingActionableTaskScrollTop() {
    const task = this.trailingActionableTask()
    if (!task) return null

    return this.taskAnchorScrollTop(task)
  }

  // Document offset that puts the top of a task card just under the sticky
  // header. 8px keeps the card's top border off the header's bottom edge.
  taskAnchorScrollTop(task) {
    const documentTop = window.scrollY + task.getBoundingClientRect().top

    return Math.max(0, documentTop - this.stickyHeaderOffset() - 8)
  }

  // Viewport height a task card actually gets: the sticky header covers the
  // top of it, so anything measured against window.innerHeight overstates it.
  availableViewportHeight() {
    return Math.max(0, window.innerHeight - this.stickyHeaderOffset() - 8)
  }

  // The last actionable card, but only when nothing follows the event that
  // owns it. If later events exist, the newest activity is what the reader
  // came for and the floating pill already offers the jump back to the task.
  trailingActionableTask() {
    const root = this.hasEventsTarget ? this.eventsTarget : this.element
    const tasks = root.querySelectorAll('fieldset[id*="inline_action"]')
    const task = tasks[tasks.length - 1]
    if (!task) return null

    const owningEvent = task.closest("[data-event-id]")
    if (!owningEvent) return null

    const hasLaterEvent = Array.from(root.querySelectorAll("[data-event-id]")).some((event) => {
      if (event === owningEvent || owningEvent.contains(event)) return false
      return Boolean(owningEvent.compareDocumentPosition(event) & Node.DOCUMENT_POSITION_FOLLOWING)
    })

    return hasLaterEvent ? null : task
  }

  stickyHeaderOffset() {
    const header = this.element.querySelector(".section-header-slim")
    if (!header || window.getComputedStyle(header).position !== "sticky") return 0

    return header.offsetHeight
  }

  // The anchor competes with the composer's ResizeObserver: a card only a
  // little taller than the viewport still leaves the page "near bottom", so
  // the clearance sync would pin the reader straight back to the bottom. Hold
  // the anchor until the reader scrolls, or briefly, whichever comes first.
  holdInitialAnchor() {
    window.clearTimeout(this.initialAnchorTimeout)
    this.initialAnchorTimeout = window.setTimeout(() => this.releaseInitialAnchor(), 1500)
  }

  releaseInitialAnchor() {
    if (!this.initialAnchorActive) return

    this.initialAnchorActive = false
    window.clearTimeout(this.initialAnchorTimeout)
  }

  // Scroll the window to the bottom of the page rather than snapping an event
  // to the top of the viewport; coalesce bursts of stream renders into one
  // smooth scroll so rapid broadcasts don't stutter.
  scrollToLatestActivity(behavior = "smooth") {
    if (this.scrollScheduled) return
    this.scrollScheduled = true

    window.requestAnimationFrame(() => {
      this.scrollScheduled = false
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior })
      this.hideNewActivityButton()
    })
  }

  scrollToNewActivity() {
    this.scrollToLatestActivity()
  }

  showNewActivityButton() {
    if (!this.hasNewActivityButtonTarget) return
    this.newActivityButtonTarget.classList.remove("hidden")
  }

  hideNewActivityButton() {
    if (!this.hasNewActivityButtonTarget) return
    this.newActivityButtonTarget.classList.add("hidden")
  }

  checkActionableTaskVisibility() {
    if (!this.hasScrollToTaskButtonTarget) return

    const actionableTasks = this.element.querySelectorAll('fieldset[id*="inline_action"]')
    if (actionableTasks.length === 0) {
      this.hideScrollToTaskButton()
      return
    }

    const lastTask = actionableTasks[actionableTasks.length - 1]
    const rect = lastTask.getBoundingClientRect()
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0

    if (isVisible) {
      this.hideScrollToTaskButton()
    } else {
      this.updateScrollToTaskButtonDirection(rect)
      if (this.topRubberBandSuppressed && rect.top >= window.innerHeight) {
        this.hideScrollToTaskButton()
        return
      }

      this.showScrollToTaskButton()
    }
  }

  showScrollToTaskButton() {
    this.scrollToTaskButtonTarget.classList.remove("hidden")
  }

  hideScrollToTaskButton() {
    this.scrollToTaskButtonTarget.classList.add("hidden")
  }

  suppressTopScrollToTaskButton() {
    if (!this.hasScrollToTaskButtonTarget) return

    this.topRubberBandSuppressed = true
    window.clearTimeout(this.topRubberBandTimeout)
    this.hideScrollToTaskButton()

    this.topRubberBandTimeout = window.setTimeout(() => {
      this.topRubberBandSuppressed = false
      this.checkActionableTaskVisibility()
    }, 360)
  }

  clearTopRubberBandSuppression() {
    if (!this.topRubberBandSuppressed) return

    this.topRubberBandSuppressed = false
    window.clearTimeout(this.topRubberBandTimeout)
    this.checkActionableTaskVisibility()
  }

  updateScrollToTaskButtonDirection(rect) {
    const isBelow = rect.top >= window.innerHeight
    const isAbove = rect.bottom <= 0
    if (!isBelow && !isAbove) return

    const direction = isBelow ? "down" : "up"

    // Down stays under the sticky header. Up sits above the fixed composer and
    // must paint above the composer's top fade.
    this.scrollToTaskButtonTarget.classList.remove("top-12", "bottom-28", "z-30", "z-40")
    this.scrollToTaskButtonTarget.classList.add(
      direction === "down" ? "top-12" : "bottom-28",
      direction === "down" ? "z-30" : "z-40"
    )

    if (this.hasScrollToTaskIconTarget) {
      this.scrollToTaskIconTarget.classList.remove("ti-arrow-down", "ti-arrow-up")
      this.scrollToTaskIconTarget.classList.add(direction === "down" ? "ti-arrow-down" : "ti-arrow-up")
    }

    this.scrollToTaskButtonTarget.querySelector("button")?.setAttribute(
      "aria-label",
      direction === "down" ? "Scroll to actionable task below" : "Scroll to actionable task above"
    )
  }

  scrollToActionableTask() {
    const actionableTasks = this.element.querySelectorAll('fieldset[id*="inline_action"]')
    const lastTask = actionableTasks[actionableTasks.length - 1]
    if (!lastTask) return

    // Centre the card in the space below the sticky header, but only with the
    // room it leaves over: a card taller than that space has no slack and lands
    // on its top, so its title and first fields are never cut off. Plain
    // block:"center" got both wrong — it measured the full viewport, so even a
    // card that fits could end up tucked under the header.
    const slack = Math.max(0, this.availableViewportHeight() - lastTask.offsetHeight)
    const top = Math.max(0, this.taskAnchorScrollTop(lastTask) - slack / 2)

    window.scrollTo({ top, behavior: "smooth" })
  }

  // The composer is fixed to the viewport bottom, so its height is reserved by
  // the timeline column's padding-bottom (the composerSpacer target), not the
  // document flow. That height is variable — the "waiting on you" blocker
  // banner, the AI-review disclaimer, and the queue notice each grow it — so
  // keep the reservation synced to the composer's real height; otherwise the
  // newest events hide behind the taller overlay.
  setupComposerClearance() {
    if (!this.hasComposerSpacerTarget || typeof ResizeObserver === "undefined") return

    this.composerResizeObserver = new ResizeObserver(() => this.syncComposerClearance())
    this.observeComposer()
  }

  // (Re)attach the observer to the current composer element. The whole form is
  // replaced on every send, so a one-time binding would leave a detached node.
  observeComposer() {
    if (!this.composerResizeObserver) return

    this.composerResizeObserver.disconnect()
    this.composerForm = document.getElementById("paperwork_event_form")
    if (!this.composerForm) return

    this.composerResizeObserver.observe(this.composerForm)
    this.syncComposerClearance()
  }

  syncComposerClearance() {
    if (!this.hasComposerSpacerTarget || !this.composerForm) return

    const wasNearBottom = this.isNearPageBottom()
    // +16px leaves a little air above the composer's top fade.
    const clearance = this.composerForm.offsetHeight + 16
    this.composerSpacerTarget.style.setProperty("--composer-clearance", `${clearance}px`)

    // The ask-user tray is fixed above the composer too, and it used to sit at a
    // static offset — so the same banners that grow the composer pushed it over
    // the tray's footer. Publish the solid box's height (the tray may paint over
    // the fade above it, but not over this) so the tray can ride on top of it.
    const composerBody = this.composerForm.querySelector("[data-composer-body]")
    if (composerBody) {
      document.documentElement.style.setProperty("--composer-body-height", `${composerBody.offsetHeight}px`)
    }

    // Keep a reader who was pinned to the bottom pinned when the composer grows
    // (e.g. the blocker banner appears), so the newest events aren't stranded
    // behind the now-taller overlay. Skip while the page-entry anchor holds:
    // the observer fires right after connect, and a tall task can still read as
    // "near bottom" from its anchored top.
    if (wasNearBottom && !this.initialAnchorActive) this.scrollToLatestActivity("auto")
  }
}
